import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { TestimonialsCarousel, TrustSection } from "@/pages/public/PublicExtra";
import { useLang } from "@/context/LangContext";
import {
  Globe, RefreshCcw, Search, MapPin, Share2, Megaphone, Palette, Sparkles,
  Brush, Camera, Video, Plane, CreditCard, Layers, Shirt, Gift, Server, Globe2,
  Bot, Smartphone, Users2, Briefcase, ArrowRight, Check, Star, Zap, TrendingUp,
  ShieldCheck, MessageCircle, CalendarClock, Trophy, Eye, Users, Clock, Monitor,
  ShoppingCart, PenTool, Cloud, Wrench, Settings, BarChart3, Headphones, BadgeCheck,
} from "lucide-react";

/* ------------------------------------------------------------ services */
export const SERVICES = [
  // 7 core Growth Stack services (homepage priority)
  { key: "website-design", name: "Web Development", icon: Globe, desc: "High-converting websites engineered to turn visitors into calls, leads, and customers.", img: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?auto=format&fit=crop&w=1000&q=70" },
  { key: "seo", name: "Local SEO", icon: Search, desc: "Rank higher in Alabama & Tennessee searches and capture buyers when they're ready.", img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1000&q=70" },
  { key: "gbp", name: "Google Business Profile", icon: MapPin, desc: "Own the local map pack with an optimised, actively-managed Google profile.", img: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=1000&q=70" },
  { key: "google-ads", name: "Google Ads", icon: Megaphone, desc: "Paid campaigns that generate qualified calls at a cost-per-lead that scales.", img: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&w=1000&q=70" },
  { key: "call-tracking", name: "Call Tracking", icon: MessageCircle, desc: "Measure every call, know which channel drove it, and never miss a lead.", img: "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=1000&q=70" },
  { key: "lead-management", name: "Lead Management", icon: Users2, desc: "Structured follow-up sequences that convert cold interest into signed customers.", img: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1000&q=70" },
  { key: "ai", name: "AI Automation", icon: Bot, desc: "AI agents that qualify leads, book appointments, and handle FAQs 24/7.", img: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1000&q=70" },

  // Secondary services (available, still shown on /services page)
  { key: "website-redesign", name: "Website Redesign", icon: RefreshCcw, desc: "Modernise your existing site into a growth-ready system.", img: "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1000&q=70" },
  { key: "social-media", name: "Social Media Management", icon: Share2, desc: "Content, scheduling & engagement across every network.", img: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&w=1000&q=70" },
  { key: "branding", name: "Branding", icon: Palette, desc: "A cohesive visual identity your customers remember.", img: "https://images.unsplash.com/photo-1522542550221-31fd19575a2d?auto=format&fit=crop&w=1000&q=70" },
  { key: "logo-design", name: "Logo Design", icon: Sparkles, desc: "A distinctive mark that anchors your entire brand.", img: "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=1000&q=70" },
  { key: "graphic-design", name: "Graphic Design", icon: Brush, desc: "Print & digital assets across every touch-point.", img: "https://images.unsplash.com/photo-1626785774625-ddcddc3445e9?auto=format&fit=crop&w=1000&q=70" },
  { key: "photography", name: "Photography", icon: Camera, desc: "Editorial photography for products, teams and spaces.", img: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=1000&q=70" },
  { key: "video", name: "Video Production", icon: Video, desc: "Cinematic brand films, ads and case studies.", img: "https://images.unsplash.com/photo-1492619375914-88005aa9e8fb?auto=format&fit=crop&w=1000&q=70" },
  { key: "drone", name: "Drone Services", icon: Plane, desc: "4K aerial photography and inspection footage.", img: "https://images.unsplash.com/photo-1508614589041-895b88991e3e?auto=format&fit=crop&w=1000&q=70" },
  { key: "nfc-cards", name: "NFC Smart Business Cards", icon: CreditCard, desc: "One tap. Everything a client needs to know about you.", img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1000&q=70" },
  { key: "business-cards", name: "Business Cards", icon: Layers, desc: "Premium printed cards with luxury finishes.", img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1000&q=70" },
  { key: "uniform", name: "Uniform Design", icon: Shirt, desc: "Custom branded uniforms your team will love wearing.", img: "https://images.unsplash.com/photo-1520975916090-3105956dac38?auto=format&fit=crop&w=1000&q=70" },
  { key: "promo", name: "Promotional Products", icon: Gift, desc: "Branded swag that turns customers into evangelists.", img: "https://images.unsplash.com/photo-1513542789411-b6a5d4f31634?auto=format&fit=crop&w=1000&q=70" },
  { key: "hosting", name: "Hosting", icon: Server, desc: "Fast, secure hosting with 99.9% uptime SLA.", img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1000&q=70" },
  { key: "domain", name: "Domain Registration", icon: Globe2, desc: "Secure the perfect domain and we manage renewals for you.", img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1000&q=70" },
  { key: "mobile", name: "Mobile Applications", icon: Smartphone, desc: "iOS + Android apps that ship on time and delight users.", img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1000&q=70" },
  { key: "crm", name: "CRM Solutions", icon: Users2, desc: "Deploy the Yosi CRM tailored to your workflow.", img: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1000&q=70" },
  { key: "consulting", name: "Business Consulting", icon: Briefcase, desc: "Growth strategy from founders who have scaled agencies.", img: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1000&q=70" },
];

// Only the 7 core Growth Stack services appear on the homepage grid.
export const GROWTH_STACK_KEYS = [
  "website-design", "seo", "gbp", "google-ads",
  "call-tracking", "lead-management", "ai",
];

/* ============================================================
   HOME — reference-image redesign
   Palette: hero #0A1530 · electric blue #2B87F5 · light #EEF1F5
   ============================================================ */

const HERO_CARDS = [
  { key: "c1", img: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?auto=format&fit=crop&w=800&q=70", cls: "md:-rotate-[4deg] md:translate-y-[10px]" },
  { key: "c2", img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=70", cls: "md:-rotate-[2deg] md:translate-y-[4px]"  },
  { key: "c3",  img: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&w=800&q=70", cls: "md:rotate-0 md:translate-y-0"             },
  { key: "c4",  img: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=800&q=70", cls: "md:rotate-[2deg] md:translate-y-[4px]"    },
  { key: "c5",  img: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=70", cls: "md:rotate-[4deg] md:translate-y-[10px]"   },
];

const TRUST_BADGES = [
  { Icon: Users, l1: "Trusted by", l2: "Businesses" },
  { Icon: Star,  l1: "5-Star",     l2: "Client Rating" },
  { Icon: Clock, l1: "24/7",       l2: "Support" },
];

const STATS = [
  { n: "150+", l: "Websites Built" },
  { n: "250+", l: "Happy Clients" },
  { n: "300+", l: "Projects Completed" },
  { n: "500+", l: "5-Star Reviews" },
];

const SERVICE_TILES = [
  { Icon: Monitor,      label: ["Premium",    "Website Design"] },
  { Icon: Search,       label: ["SEO & Google", "Business Profile"] },
  { Icon: ShoppingCart, label: ["E-Commerce", "Solutions"] },
  { Icon: Smartphone,   label: ["Mobile Apps", "& Systems"] },
  { Icon: Bot,          label: ["AI & Business", "Automation"] },
  { Icon: MapPin,       label: ["Local SEO",  "& Rankings"] },
  { Icon: Palette,      label: ["Branding &", "Graphic Design"] },
  { Icon: PenTool,      label: ["Content &",  "Copywriting"] },
  { Icon: Megaphone,    label: ["Marketing &", "Social Media"] },
  { Icon: Cloud,        label: ["Cloud Hosting", "& Security"] },
  { Icon: Wrench,       label: ["Maintenance", "& Support"] },
];

const DIFFERENTIATORS = [
  { Icon: Settings,    l1: "Custom",       l2: "Solutions" },
  { Icon: BarChart3,   l1: "Data-Driven",  l2: "Approach" },
  { Icon: TrendingUp,  l1: "Results",      l2: "That Matter" },
  { Icon: Headphones,  l1: "Dedicated",    l2: "Support" },
  { Icon: BadgeCheck,  l1: "Always The",   l2: "Best Price" },
];

const TRUSTED_BY_CLIENTS = [
  "Top of the Line Tree Services",
  "Noyola Construction",
  "Huntsville American Fence",
  "Lara Construction",
  "Clínica Médica Moscati CMMM",
];

/* ------------------------- Trusted-By Marquee ------------------------- */
function TrustedByMarquee() {
  const [clients, setClients] = useState(null);
  useEffect(() => {
    api.get("/public/trusted-clients")
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch(() => setClients([]));
  }, []);

  // While loading, render nothing (avoids flash). If admin removed all clients, hide the strip.
  if (clients === null) return null;
  if (clients.length === 0) return null;

  // Duplicate list so the seamless loop always has content off-screen
  const loop = [...clients, ...clients];
  return (
    <section
      className="relative py-14 overflow-hidden"
      style={{ background: "linear-gradient(180deg,#0A1530 0%,#0C1839 100%)" }}
      data-testid="trusted-by-marquee"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px"
           style={{ background: "linear-gradient(90deg,transparent,rgba(43,135,245,0.4),transparent)" }} />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
           style={{ background: "linear-gradient(90deg,transparent,rgba(43,135,245,0.4),transparent)" }} />

      <div className="text-center mb-8 px-6">
        <div className="text-[11px] uppercase tracking-[0.35em] font-bold" style={{ color: "#2B87F5" }}>
          Trusted By Local Businesses &amp; Organizations
        </div>
      </div>

      <div className="trust-marquee relative">
        {/* Edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10"
             style={{ background: "linear-gradient(90deg,#0A1530 0%,transparent 100%)" }} />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10"
             style={{ background: "linear-gradient(270deg,#0A1530 0%,transparent 100%)" }} />

        <div className="trust-marquee-track flex items-center gap-12 md:gap-16 whitespace-nowrap w-max">
          {loop.map((c, i) => {
            const label = c.name || "";
            const content = (
              <>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#2B87F5", boxShadow: "0 0 12px rgba(43,135,245,0.7)" }} />
                <span className="font-display font-bold text-white text-lg md:text-xl tracking-wide">
                  {label}
                </span>
              </>
            );
            return c.website_url ? (
              <a
                key={`${c.client_id}-${i}`}
                href={c.website_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`trusted-by-item-${i}`}
                className="flex items-center gap-4 shrink-0 hover:opacity-90"
              >
                {content}
              </a>
            ) : (
              <div
                key={`${c.client_id}-${i}`}
                data-testid={`trusted-by-item-${i}`}
                className="flex items-center gap-4 shrink-0"
              >
                {content}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Hero -------------------------- */
function HeroSection() {
  const { t } = useLang();
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "linear-gradient(180deg,#0A1530 0%,#08122A 100%)" }}
      data-testid="hero"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-24 right-[8%] w-[520px] h-[380px] rounded-full blur-[110px]"
             style={{ background: "rgba(43,135,245,0.28)" }} />
        <div className="absolute -bottom-20 left-[-6%] w-[420px] h-[300px] rounded-full blur-[120px]"
             style={{ background: "rgba(30,124,245,0.18)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5 animate-fade-in-up">
          <p className="italic text-lg font-medium mb-4" style={{ color: "#2B87F5" }}>{t("public.hero.overline")}</p>
          <h1 className="font-display font-black text-white leading-[0.9] tracking-[-0.04em] text-5xl sm:text-6xl md:text-7xl lg:text-[80px]">
            {t("public.hero.h1a")}<br />
            {t("public.hero.h1b")}<br />
            <span style={{ color: "#2B87F5" }}>{t("public.hero.h1c")}</span>
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed" style={{ color: "#C9D3E4" }}>{t("public.hero.support")}</p>
          <p className="mt-3 text-xs uppercase tracking-widest font-semibold" style={{ color: "#7DBFFF" }}>{t("public.hero.location")}</p>

          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/contact?audit=1" data-testid="hero-cta-primary"
              className="btn-scale group h-12 pl-6 pr-2 rounded-full text-white font-bold text-sm inline-flex items-center gap-3 shadow-[0_16px_40px_rgba(43,135,245,0.45)]"
              style={{ background: "#2B87F5" }}>
              {t("public.hero.cta_primary")}
              <span className="w-9 h-9 grid place-items-center rounded-full bg-white/15 group-hover:bg-white/25 transition-colors">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link to="/contact/strategy-call" data-testid="hero-cta-secondary"
              className="btn-scale h-12 px-6 rounded-full border border-white/20 text-white font-semibold text-sm inline-flex items-center gap-2 hover:border-white/50 hover:bg-white/5">
              <CalendarClock className="w-4 h-4" /> {t("public.hero.cta_secondary")}
            </Link>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-12 max-w-md">
            {[["l1a","l1b",Users],["l2a","l2b",Star],["l3a","l3b",Clock]].map(([k1,k2,Icon], i) => (
              <div key={k1} className="flex items-center gap-3" data-testid={`hero-trust-${i}`}>
                <div className="w-11 h-11 rounded-full border border-slate-700 grid place-items-center shrink-0" style={{ color: "#2B87F5" }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-white text-xs leading-tight font-medium">
                  <div>{t(`public.hero.trust.${k1}`)}</div>
                  <div className="opacity-80">{t(`public.hero.trust.${k2}`)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 relative" data-testid="hero-visual">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[80%] h-[65%] rounded-full blur-[100px]" style={{ background: "rgba(43,135,245,0.35)" }} />
          </div>
          <div className="relative flex md:gap-3 gap-2 items-end justify-center flex-col md:flex-row">
            {HERO_CARDS.map((c, i) => {
              const label = t(`public.hero.cards.${c.key}`);
              return (
                <div key={c.key} data-testid={`hero-card-${i}`}
                  className={`relative md:w-[19%] w-full md:aspect-[9/19] aspect-[16/9] rounded-2xl overflow-hidden border border-blue-500/25 shadow-[0_20px_60px_rgba(43,135,245,0.35)] transition-transform duration-500 hover:-translate-y-2 ${c.cls} animate-fade-in-up delay-${i + 1}`}
                  style={{ background: "#0F1B36" }}>
                  <img src={c.img} alt={label} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1530] via-[#0A1530]/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="text-white uppercase font-bold tracking-wider text-[10px] md:text-[11px] leading-tight">{label}</div>
                  </div>
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/10 backdrop-blur border border-white/20 grid place-items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Real Results Panel ------------------------- */
function RealResultsPanel() {
  const { t } = useLang();
  return (
    <section className="relative -mt-16 mb-4 z-10" data-testid="real-results">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="rounded-3xl border border-blue-500/20 px-8 py-10 md:px-12 md:py-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center shadow-[0_30px_80px_rgba(10,21,48,0.6)]"
             style={{ background: "linear-gradient(180deg,#0A1530 0%,#0C1839 100%)" }}>
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "#2B87F5" }}>{t("public.results.over")}</div>
            <h2 className="text-white font-display text-2xl md:text-3xl leading-tight font-bold">
              {t("public.results.h2a")}<br />
              <span style={{ color: "#2B87F5" }}>{t("public.results.h2b")}</span>
            </h2>
          </div>
          <div className="md:col-span-7 text-sm leading-relaxed" style={{ color: "#C9D3E4" }}>
            <p className="mb-3">{t("public.results.p1")}</p>
            <p className="text-xs" style={{ color: "#8A96AC" }}>{t("public.results.p2")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Growth System (7-node flow) ------------------------- */
const GROWTH_FLOW = [
  { l: "Website" },
  { l: "Google Visibility" },
  { l: "Calls" },
  { l: "Leads" },
  { l: "Follow-Up" },
  { l: "Conversion" },
  { l: "Optimization" },
];

function GrowthSystemSection() {
  const { t } = useLang();
  const nodes = t("public.growth.nodes") || GROWTH_FLOW.map((n) => n.l);
  return (
    <section className="py-24 px-6 sm:px-8" style={{ background: "#0A1530" }} data-testid="growth-system">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.35em] font-bold mb-3" style={{ color: "#2B87F5" }}>{t("public.growth.over")}</div>
          <h2 className="font-display font-black text-white text-3xl md:text-5xl leading-tight">
            {t("public.growth.h2a")} <span style={{ color: "#2B87F5" }}>{t("public.growth.h2b")}</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed" style={{ color: "#C9D3E4" }} dangerouslySetInnerHTML={{ __html: t("public.growth.lede") }} />
        </div>
        <div className="flex flex-wrap justify-center items-center gap-3">
          {(Array.isArray(nodes) ? nodes : GROWTH_FLOW.map((n)=>n.l)).map((label, i, arr) => (
            <React.Fragment key={label}>
              <div data-testid={`growth-node-${i}`}
                   className="px-5 h-12 rounded-full border border-blue-500/40 bg-blue-500/10 text-white text-sm font-semibold flex items-center whitespace-nowrap shadow-[0_0_20px_rgba(43,135,245,0.25)]">
                {label}
              </div>
              {i < arr.length - 1 && <ArrowRight className="w-4 h-4" style={{ color: "rgba(43,135,245,0.55)" }} />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Growth Stack Services (7 cards) ------------------------- */
function GrowthStackServices() {
  const { t } = useLang();
  const growth = SERVICES.filter((s) => GROWTH_STACK_KEYS.includes(s.key));
  return (
    <section className="py-24 px-6 sm:px-8" style={{ background: "#EEF1F5" }} data-testid="growth-services">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: "#2B87F5" }}>{t("public.services.over")}</div>
          <h2 className="font-display font-black text-3xl md:text-5xl leading-tight" style={{ color: "#0F172A" }}>
            {t("public.services.h2a")}<br />
            <span style={{ color: "#2B87F5" }}>{t("public.services.h2b")}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {growth.map((s) => {
            const name = t(`public.services.list.${s.key}.name`) || s.name;
            const desc = t(`public.services.list.${s.key}.desc`) || s.desc;
            return (
              <div key={s.key} data-testid={`growth-svc-${s.key}`}
                   className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all flex flex-col">
                <div className="w-12 h-12 rounded-xl grid place-items-center mb-4" style={{ background: "rgba(43,135,245,0.08)" }}>
                  <s.icon className="w-6 h-6" strokeWidth={1.75} style={{ color: "#2B87F5" }} />
                </div>
                <h3 className="font-display font-bold text-lg mb-2" style={{ color: "#0F172A" }}>{name}</h3>
                <p className="text-sm leading-relaxed mb-5 flex-1" style={{ color: "#475569" }}>{desc}</p>
                <Link to={`/services/${s.key}`} className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: "#2B87F5" }}>
                  {t("public.services.learn")} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-14">
          <Link to="/services" data-testid="growth-services-cta"
                className="btn-scale inline-flex items-center gap-2 h-12 px-7 rounded-full font-bold text-sm text-white shadow-[0_14px_36px_rgba(43,135,245,0.4)]"
                style={{ background: "#2B87F5" }}>
            {t("public.services.cta")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Industries We Serve ------------------------- */
const INDUSTRIES = [
  { name: "Contractors",         Icon: Wrench },
  { name: "Tree Services",       Icon: Sparkles },
  { name: "Construction",        Icon: Layers },
  { name: "Medical Clinics",     Icon: Star },
  { name: "Home Services",       Icon: ShieldCheck },
  { name: "Local Businesses",    Icon: MapPin },
];

function IndustriesSection() {
  const { t } = useLang();
  const labels = t("public.industries.list");
  return (
    <section className="py-24 px-6 sm:px-8" style={{ background: "#0A1530" }} data-testid="industries">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: "#2B87F5" }}>{t("public.industries.over")}</div>
          <h2 className="font-display font-black text-white text-3xl md:text-5xl leading-tight">
            {t("public.industries.h2a")}<br />
            <span style={{ color: "#2B87F5" }}>{t("public.industries.h2b")}</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {INDUSTRIES.map(({ Icon }, i) => (
            <div key={i} data-testid={`industry-${i}`}
                 className="rounded-xl border border-blue-500/20 bg-slate-900/40 p-5 text-center hover:border-blue-500/60 hover:bg-blue-500/5 transition-colors">
              <div className="w-12 h-12 mx-auto rounded-xl grid place-items-center mb-3"
                   style={{ background: "rgba(43,135,245,0.1)", color: "#7DBFFF" }}>
                <Icon className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <div className="text-white text-sm font-semibold">{Array.isArray(labels) ? labels[i] : INDUSTRIES[i].name}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowWeWorkSection() {
  const { t } = useLang();
  const steps = t("public.process.steps");
  const arr = Array.isArray(steps) ? steps : PROCESS;
  return (
    <section className="py-24 px-6 sm:px-8 bg-slate-950" data-testid="how-we-work">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: "#2B87F5" }}>{t("public.process.over")}</div>
          <h2 className="font-display font-black text-white text-3xl md:text-5xl leading-tight">
            {t("public.process.h2a")} <span style={{ color: "#2B87F5" }}>{t("public.process.h2b")}</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {arr.map((s, i) => (
            <div key={s.t} data-testid={`process-${i}`}
                 className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 hover:border-blue-500/40 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 grid place-items-center rounded-full text-sm font-black"
                      style={{ background: "rgba(43,135,245,0.15)", color: "#7DBFFF" }}>{i + 1}</span>
                <h3 className="font-display font-bold text-lg text-white">{s.t}</h3>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: "#C9D3E4" }}>{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------- How We Work (6-step process) ------------------------- */
const PROCESS = [
  { t: "Audit",       d: "Review your website, Google presence, calls, and current lead flow." },
  { t: "Strategy",    d: "Identify the highest-leverage actions and priority-rank them." },
  { t: "Build",       d: "Ship the assets — site, profile, ads, tracking, automation." },
  { t: "Launch",      d: "Coordinate a clean cutover with QA across every touch-point." },
  { t: "Track",       d: "Measure real traffic, calls, and leads through verified tools." },
  { t: "Optimize",    d: "Iterate monthly against the numbers that actually move revenue." },
];

function HowWeWorkSection_OLD_UNUSED() {
  return null;
}

/* ------------------------- Case Studies teaser ------------------------- */
function CaseStudiesTeaser() {
  const { t } = useLang();
  return (
    <section className="py-20 px-6 sm:px-8 bg-slate-950 border-t border-slate-800/60" data-testid="case-studies-teaser">
      <div className="max-w-4xl mx-auto text-center">
        <div className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: "#2B87F5" }}>{t("public.cases.over")}</div>
        <h2 className="font-display font-black text-white text-3xl md:text-4xl leading-tight mb-4">
          {t("public.cases.h2a")} <span style={{ color: "#2B87F5" }}>{t("public.cases.h2b")}</span>
        </h2>
        <p className="text-base leading-relaxed max-w-2xl mx-auto mb-8" style={{ color: "#C9D3E4" }}>
          {t("public.cases.lede")}
        </p>
        <Link to="/case-studies" data-testid="case-studies-cta"
              className="btn-scale inline-flex items-center gap-2 h-11 px-6 rounded-full text-white font-bold text-sm border border-white/25 hover:border-white/60">
          {t("public.cases.cta")} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

/* ------------------------- Trust / Company Info ------------------------- */
function TrustCompanyInfo() {
  const { t } = useLang();
  const cards = [
    { Icon: MapPin, label: t("public.trust.based"), lines: [t("public.trust.loc"), t("public.trust.serve")] },
    { Icon: MessageCircle, label: t("public.trust.call"), links: [{ href: "tel:+12562888270", text: "256-288-8270" }] },
    { Icon: Star, label: t("public.trust.email"), links: [{ href: "mailto:info@yosimediastudio.com", text: "info@yosimediastudio.com" }] },
  ];
  return (
    <section className="py-20 px-6 sm:px-8" style={{ background: "linear-gradient(180deg,#0A1530 0%,#0C1839 100%)" }} data-testid="trust-company">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        {cards.map(({ Icon, label, lines, links }) => (
          <div key={label} className="rounded-xl border border-blue-500/20 bg-slate-900/40 p-6 text-center">
            <div className="w-11 h-11 mx-auto rounded-xl grid place-items-center mb-3" style={{ background: "rgba(43,135,245,0.15)", color: "#7DBFFF" }}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: "#2B87F5" }}>{label}</div>
            {lines && lines.map((line, i) => (
              <div key={i} className="text-white text-sm">{line}</div>
            ))}
            {links && links.map((line, i) => (
              <a key={i} href={line.href} className="text-white text-sm font-semibold hover:text-blue-300 block">{line.text}</a>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------- Final CTA ------------------------- */
function FinalCta() {
  const { t } = useLang();
  return (
    <section className="py-24 px-6 sm:px-8 bg-slate-950" data-testid="final-cta">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-white leading-tight mb-4">
          {t("public.final.h2a")} <span style={{ color: "#2B87F5" }}>{t("public.final.h2b")}</span>
        </h2>
        <p className="text-base mb-10 max-w-2xl mx-auto" style={{ color: "#C9D3E4" }}>{t("public.final.lede")}</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link to="/contact?audit=1" data-testid="final-cta-audit"
                className="btn-scale h-12 px-7 rounded-full font-bold text-sm text-white inline-flex items-center gap-2 shadow-[0_14px_36px_rgba(43,135,245,0.45)]"
                style={{ background: "#2B87F5" }}>
            {t("public.final.audit")} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact/strategy-call" data-testid="final-cta-call"
                className="btn-scale h-12 px-7 rounded-full font-bold text-sm text-white border border-white/25 hover:border-white/60 inline-flex items-center gap-2">
            <CalendarClock className="w-4 h-4" /> {t("public.final.call")}
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Lower Dark CTA ------------------------- */
function LowerCta() {
  return (
    <section
      className="relative overflow-hidden py-24 px-6 sm:px-8"
      style={{ background: "linear-gradient(135deg,#0A1530 0%,#0C1B3E 100%)" }}
      data-testid="lower-cta"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[420px] rounded-full blur-[120px]"
             style={{ background: "rgba(43,135,245,0.18)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Left copy */}
        <div className="lg:col-span-3">
          <div className="text-xs uppercase tracking-[0.3em] font-bold mb-4" style={{ color: "#2B87F5" }}>About Yosi Studio</div>
          <h2 className="font-display font-black text-white text-3xl md:text-4xl leading-tight">
            We Focus On Your Business,<br />
            <span style={{ color: "#2B87F5" }}>We Handle The Rest.</span>
          </h2>
          <p className="mt-5 text-sm md:text-base" style={{ color: "#C9D3E4" }}>
            Your success is our mission. We build, manage, and optimize everything so you can focus on what you do best.
          </p>
          <div className="mt-8 flex items-center gap-4">
            <Link to="/about" data-testid="lower-cta-about"
                  className="btn-scale text-white text-sm font-semibold border-b-2 border-blue-400 pb-1 hover:border-white">
              Learn more about us
            </Link>
          </div>
        </div>

        {/* Middle differentiators */}
        <div className="lg:col-span-6 flex items-start justify-between gap-2 md:gap-4">
          {DIFFERENTIATORS.map(({ Icon, l1, l2 }, i, arr) => (
            <React.Fragment key={l1}>
              <div className="flex flex-col items-center text-center flex-1 min-w-0" data-testid={`lower-cta-diff-${i}`}>
                <div className="w-16 h-16 rounded-full grid place-items-center mb-3 shadow-[0_0_30px_rgba(43,135,245,0.4)] transition-transform hover:scale-110 shrink-0"
                     style={{ border: "2px solid rgba(43,135,245,0.6)", background: "rgba(43,135,245,0.08)", color: "#7DBFFF" }}>
                  <Icon className="w-6 h-6" strokeWidth={1.75} />
                </div>
                <div className="text-white text-[11px] md:text-xs font-semibold leading-tight whitespace-nowrap">{l1}<br />{l2}</div>
              </div>
              {i < arr.length - 1 && (
                <ArrowRight className="w-4 h-4 mt-6 shrink-0" style={{ color: "rgba(43,135,245,0.5)" }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Right CTA */}
        <div className="lg:col-span-3 lg:text-right">
          <h3 className="font-display font-black text-white text-2xl md:text-[26px] leading-tight mb-3">
            Ready to Take Your Business to the <span style={{ color: "#2B87F5" }}>Next Level?</span>
          </h3>
          <p className="text-sm mb-6" style={{ color: "#8A96AC" }}>Let's build something amazing together.</p>
          <Link
            to="/contact"
            data-testid="lower-cta-primary"
            className="btn-scale group h-14 pl-6 pr-3 rounded-full inline-flex items-center gap-3 text-white font-bold text-xs md:text-sm uppercase tracking-wider shadow-[0_20px_50px_rgba(43,135,245,0.55)]"
            style={{ background: "#2B87F5" }}
          >
            GET FREE CONSULTATION
            <span className="w-10 h-10 rounded-full bg-white/15 grid place-items-center group-hover:bg-white/25 transition-colors">
              <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- PublicHome --------------------------- */
export function PublicHome() {
  return (
    <MarketingLayout>
      <HeroSection />
      <TrustedByMarquee />
      <RealResultsPanel />
      <GrowthSystemSection />
      <GrowthStackServices />
      <IndustriesSection />
      <HowWeWorkSection />
      <CaseStudiesTeaser />
      <TrustCompanyInfo />
      <TestimonialsCarousel />
      <FinalCta />
    </MarketingLayout>
  );
}

/* ------------------------------------------------------------ services */
export function PublicServices() {
  return (
    <MarketingLayout>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20" data-testid="services-page">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Services</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">Everything your business needs.</h1>
        <p className="text-slate-400 max-w-2xl mb-12 leading-relaxed">22 in-house services delivered by senior specialists. From brand launch to enterprise-grade automation — one team, one point of contact.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s) => (
            <div key={s.key} className="group rounded-xl border border-slate-800 bg-slate-900/40 p-6 hover:border-blue-500/50 hover:bg-slate-900/70 transition-all flex flex-col" data-testid={`svc-${s.key}`}>
              <div className="h-32 rounded-lg bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-slate-800/60 mb-5 grid place-items-center">
                <s.icon className="w-10 h-10 text-blue-400 opacity-80 group-hover:opacity-100 transition-opacity" />
              </div>
              <h3 className="font-display font-semibold text-lg mb-2 text-slate-100">{s.name}</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-4 flex-1">{s.desc}</p>
              <div className="flex gap-2 mt-auto">
                <Link to={`/services/${s.key}`} className="btn-scale flex-1 h-9 rounded-md border border-slate-700 text-slate-200 text-xs font-medium hover:bg-slate-800 grid place-items-center">Learn More</Link>
                <Link to={`/contact?service=${encodeURIComponent(s.name)}`} data-testid={`svc-quote-${s.key}`} className="btn-scale flex-1 h-9 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-xs font-medium grid place-items-center">Request Quote</Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}

/* ------------------------------------------------------------ portfolio */
const PORTFOLIO = [
  { key: "fashion-01", name: "Nova Atelier", industry: "Fashion & E-commerce", services: ["Website Design", "Branding", "Photography", "SEO"] },
  { key: "saas-01", name: "Vertex Business Consulting", industry: "Professional Services", services: ["CRM Solutions", "Branding", "Automation"] },
  { key: "restaurant-01", name: "Coastal Bistro", industry: "Restaurant", services: ["Website Design", "Social Media Marketing", "Photography", "Branding"] },
  { key: "clinic-01", name: "Downtown Medical Clinic", industry: "Healthcare", services: ["Website Design", "SEO", "AI Automation"] },
  { key: "retail-01", name: "Anchor Retail Group", industry: "Retail", services: ["Website Design", "Branding", "Promotional Products"] },
  { key: "realestate-01", name: "Highland Realty Partners", industry: "Real Estate", services: ["Website Design", "Drone Photography", "Video Production"] },
];

export function PublicPortfolio() {
  return (
    <MarketingLayout>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20" data-testid="portfolio-page">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Our Work</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">Real businesses. Real results.</h1>
        <p className="text-slate-400 max-w-2xl mb-12 leading-relaxed">Every case study starts with a business goal and ends with measurable outcomes — leads, bookings, revenue.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PORTFOLIO.map((p) => (
            <Link key={p.key} to={`/portfolio/${p.key}`} className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden group hover:border-blue-500/50 transition-all block" data-testid={`case-${p.key}`}>
              <div className="grid grid-cols-2 h-56">
                <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 border-r border-slate-800 grid place-items-center">
                  <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-900/70 px-2 py-0.5 rounded">Before</span>
                  <div className="text-slate-600 text-xs">Before image</div>
                </div>
                <div className="relative bg-gradient-to-br from-blue-500/20 to-cyan-500/10 grid place-items-center">
                  <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest text-blue-300 bg-blue-500/20 px-2 py-0.5 rounded">After</span>
                  <div className="text-slate-300 text-xs">After image</div>
                </div>
              </div>
              <div className="p-5">
                <div className="text-xs text-blue-400 uppercase tracking-widest mb-1">{p.industry}</div>
                <h3 className="font-display font-semibold text-lg text-slate-100 mb-3">{p.name}</h3>
                <div className="flex flex-wrap gap-1.5">
                  {p.services.map((s) => (
                    <span key={s} className="text-[11px] px-2 py-0.5 rounded-md bg-slate-800/70 text-slate-300 border border-slate-700">{s}</span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}

/* ------------------------------------------------------------ pricing */
const PLANS = [
  {
    key: "starter", name: "Starter", price: "$1,499", cta: "Request Quote",
    tagline: "For businesses launching online.",
    features: ["Custom website up to 5 pages", "Mobile-optimised design", "Contact form + Google Maps", "Basic on-page SEO", "1 round of revisions"],
  },
  {
    key: "professional", name: "Professional", price: "$3,499", cta: "Request Quote", highlight: true,
    tagline: "For growing businesses ready to scale.",
    features: ["Custom website up to 10 pages", "Advanced SEO optimization", "Google Business Profile setup", "Content management system", "Analytics + conversion tracking", "3 rounds of revisions"],
  },
  {
    key: "enterprise", name: "Enterprise", price: "Custom", cta: "Talk to Sales",
    tagline: "For established brands needing an operating system.",
    features: ["Everything in Professional", "AI Automation & chatbots", "CRM + client portal", "Marketing campaigns included", "Monthly strategy sessions", "Dedicated account manager", "24/7 priority support"],
  },
];

export function PublicPricing() {
  return (
    <MarketingLayout>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20" data-testid="pricing-page">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Pricing</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">Transparent, outcome-based pricing.</h1>
        <p className="text-slate-400 max-w-2xl mb-12 leading-relaxed">Every project is quoted based on scope, not hours. Pick a starting point and we'll tailor the perfect scope with you.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <div key={p.key} className={`rounded-xl border p-6 flex flex-col ${p.highlight ? "border-blue-500 bg-blue-500/5 shadow-xl shadow-blue-500/20 relative" : "border-slate-800 bg-slate-900/40"}`} data-testid={`plan-${p.key}`}>
              {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-widest px-3 py-1 bg-blue-500 text-white rounded-full">Most popular</span>}
              <h3 className="font-display font-semibold text-2xl mb-1">{p.name}</h3>
              <p className="text-sm text-slate-400 mb-6">{p.tagline}</p>
              <div className="mb-6"><span className="text-4xl font-bold">{p.price}</span>{p.price !== "Custom" && <span className="text-sm text-slate-500 ml-1">starting</span>}</div>
              <ul className="space-y-2.5 text-sm text-slate-300 mb-8 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Link to={`/contact?plan=${p.key}`} data-testid={`plan-cta-${p.key}`} className={`btn-scale h-11 rounded-md font-semibold text-sm grid place-items-center ${p.highlight ? "bg-blue-500 hover:bg-blue-400 text-white shadow-lg shadow-blue-500/40" : "border border-slate-700 hover:bg-slate-800 text-slate-100"}`}>
                {p.cta}
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center mt-14 text-sm text-slate-400">
          Need something bespoke? <Link to="/contact" className="text-blue-400 hover:text-blue-300">Get a custom quote</Link>.
        </div>
      </section>
    </MarketingLayout>
  );
}

/* ------------------------------------------------------------ about */
export function PublicAbout() {
  return (
    <MarketingLayout>
      <section className="max-w-4xl mx-auto px-6 sm:px-8 py-20" data-testid="about-page">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">About us</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mb-6">We're the studio behind the businesses you admire.</h1>
        <p className="text-lg text-slate-300 leading-relaxed mb-14">
          Yosi Media Studio was founded on a simple idea: the fragmented agency model is broken. Clients shouldn't juggle 6 vendors for one brand launch. We built one integrated team that handles every touch-point — design, engineering, marketing, and operations — so our clients ship faster and grow harder.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
          {[
            { Icon: Zap, t: "Mission", d: "Turn ambitious ideas into unstoppable businesses through world-class design, marketing and technology." },
            { Icon: Star, t: "Vision", d: "To be the default operating system of ambitious agencies, brands, and entrepreneurs across the Americas." },
            { Icon: ShieldCheck, t: "Values", d: "Craft over quantity. Honesty over optics. Long-term partnerships over transactions. Ownership over hand-offs." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
              <div className="w-10 h-10 grid place-items-center rounded-md bg-blue-500/10 text-blue-400 mb-4"><Icon className="w-5 h-5" /></div>
              <h3 className="font-display font-semibold text-lg mb-2">{t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        <h2 className="font-display text-2xl sm:text-3xl font-bold mb-6">Why choose Yosi Media Studio</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            "One integrated team — no more juggling vendors.",
            "Senior specialists only — no juniors on your account.",
            "Fixed timelines & transparent pricing.",
            "Bilingual: English & Spanish across every deliverable.",
            "In-house CRM & client portal included.",
            "24/7 support after launch.",
          ].map((w) => (
            <li key={w} className="flex items-start gap-2 rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-300">
              <Check className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" /> {w}
            </li>
          ))}
        </ul>
      </section>
    </MarketingLayout>
  );
}

/* ------------------------------------------------------------ contact */
export function PublicContact() {
  const { t, lang } = useLang();
  const params = new URLSearchParams(window.location.search);
  const isAudit = params.get("audit") === "1";
  const isCall  = params.get("call") === "1";
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    service_needed: params.get("service") || "",
    budget: "",
    city: "", state: "", industry: "", monthly_leads: "", primary_goal: "", website: "",
    message: params.get("plan")
      ? `I'm interested in the ${params.get("plan")} plan.`
      : (isCall ? "I'd like to book a strategy call." : ""),
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/public/site-config").then(({ data }) => setCfg(data)).catch(() => setCfg({}));
  }, []);

  const kind = isAudit ? "audit" : isCall ? "call" : "get";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const extras = [];
      if (form.website) extras.push(`Website: ${form.website}`);
      if (form.city || form.state) extras.push(`Location: ${[form.city, form.state].filter(Boolean).join(", ")}`);
      if (form.industry) extras.push(`Industry: ${form.industry}`);
      if (form.primary_goal) extras.push(`Primary goal: ${form.primary_goal}`);
      if (form.monthly_leads) extras.push(`Approx monthly calls/leads: ${form.monthly_leads}`);
      const composedMessage = [
        form.message || form.service_needed || (isAudit ? "Requesting a free growth audit." : "New enquiry from the marketing website."),
        extras.length ? `\n\n— Growth Audit details —\n${extras.join("\n")}` : "",
      ].join("");
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        message: composedMessage,
        company: form.company || null,
        service_needed: form.service_needed || null,
        budget: form.budget || null,
        source: isAudit ? "growth_audit_form" : (isCall ? "strategy_call_form" : "website_contact_form"),
        lang,
      });
      toast.success(t("public.contact.toast.success"));
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || (cfg?.contact_email ? `${t("public.contact.toast.email_error")} ${cfg.contact_email}` : t("public.contact.toast.generic_error")));
    } finally {
      setSending(false);
    }
  };

  return (
    <MarketingLayout>
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-10" data-testid="contact-page">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">{t(`public.contact.overline.${kind}`)}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">
            {t(`public.contact.title.${kind}`)}
          </h1>
          <p className="text-slate-400 leading-relaxed mb-8">
            {t(`public.contact.lede.${kind}`)}
          </p>

          <ul className="space-y-4 text-sm text-slate-300">
            <li className="flex items-center gap-3"><span className="w-8 h-8 grid place-items-center rounded-md bg-blue-500/10 text-blue-400"><MessageCircle className="w-4 h-4" /></span> {cfg?.contact_email ? <a href={`mailto:${cfg.contact_email}`} className="hover:text-white" data-testid="contact-email-link">{cfg.contact_email}</a> : <span className="text-slate-400 italic" data-testid="contact-email-fallback">{t("public.contact.perks.email_soon")}</span>}</li>
            <li className="flex items-center gap-3"><span className="w-8 h-8 grid place-items-center rounded-md bg-blue-500/10 text-blue-400"><Star className="w-4 h-4" /></span> {t("public.contact.perks.call")}</li>
            <li className="flex items-center gap-3"><span className="w-8 h-8 grid place-items-center rounded-md bg-blue-500/10 text-blue-400"><ShieldCheck className="w-4 h-4" /></span> {t("public.contact.perks.no_pressure")}</li>
          </ul>
        </div>

        {sent ? (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center" data-testid="contact-success">
            <Check className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold mb-2">{t("public.contact.success.title")}</h2>
            <p className="text-slate-400 text-sm">{t("public.contact.success.body")}{cfg?.contact_email ? ` (${cfg.contact_email})` : ""}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.fields.name")}</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" /></label>
              <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.fields.company")}</span>
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-company" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" /></label>
              <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.fields.email")}</span>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" /></label>
              <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.fields.phone")}</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" /></label>
              <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.fields.service")}</span>
                <select value={form.service_needed} onChange={(e) => setForm({ ...form, service_needed: e.target.value })} data-testid="contact-service" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-2 text-sm focus:border-blue-500 outline-none">
                  <option value="">{t("public.contact.fields.service_choose")}</option>
                  {SERVICES.map((s) => <option key={s.key} value={s.name}>{t(`public.services.list.${s.key}.name`) || s.name}</option>)}
                  <option value="Multiple">{t("public.contact.fields.service_multi")}</option>
                </select></label>
              <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.fields.budget")}</span>
                <select value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="contact-budget" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-2 text-sm focus:border-blue-500 outline-none">
                  <option value="">{t("public.contact.fields.budget_choose")}</option>
                  <option>Under $2,000</option>
                  <option>$2,000 – $5,000</option>
                  <option>$5,000 – $10,000</option>
                  <option>$10,000 – $25,000</option>
                  <option>$25,000+</option>
                </select></label>
            </div>
            <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.fields.message")}</span>
              <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-blue-500 outline-none" placeholder={t("public.contact.fields.message_placeholder")} /></label>

            {isAudit && (
              <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4 space-y-4" data-testid="audit-fields">
                <div className="text-xs uppercase tracking-widest text-blue-400 font-bold">{t("public.contact.audit_section.title")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.audit_section.website")}</span>
                    <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://yourbusiness.com" data-testid="audit-website" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" /></label>
                  <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.audit_section.industry")}</span>
                    <input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder={t("public.contact.audit_section.industry_placeholder")} data-testid="audit-industry" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" /></label>
                  <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.audit_section.city")}</span>
                    <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="audit-city" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" /></label>
                  <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.audit_section.state")}</span>
                    <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="audit-state" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-2 text-sm focus:border-blue-500 outline-none">
                      <option value="">{t("public.contact.audit_section.state_choose")}</option>
                      <option>Alabama</option>
                      <option>Tennessee</option>
                      <option>Other</option>
                    </select></label>
                  <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.audit_section.monthly_leads")}</span>
                    <input value={form.monthly_leads} onChange={(e) => setForm({ ...form, monthly_leads: e.target.value })} placeholder={t("public.contact.audit_section.monthly_leads_placeholder")} data-testid="audit-leads" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" /></label>
                  <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{t("public.contact.audit_section.primary_goal")}</span>
                    <input value={form.primary_goal} onChange={(e) => setForm({ ...form, primary_goal: e.target.value })} placeholder={t("public.contact.audit_section.primary_goal_placeholder")} data-testid="audit-goal" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" /></label>
                </div>
              </div>
            )}

            <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-scale w-full h-11 rounded-md bg-blue-500 hover:bg-blue-400 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 shadow-lg shadow-blue-500/40 disabled:opacity-50">
              {sending ? t("public.contact.submit.sending") : t(`public.contact.submit.${kind}`)}
            </button>
            <p className="text-[11px] text-slate-500 text-center">{t("public.contact.legal")}</p>
          </form>
        )}
      </section>

      {/* WhatsApp + Calendly conversion row */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 pb-16 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="contact-conversion">
        {/* WhatsApp CTA */}
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="contact-whatsapp-card">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 grid place-items-center rounded-md bg-emerald-500/15 text-emerald-400"><MessageCircle className="w-5 h-5" /></span>
            <div>
              <h3 className="font-display font-semibold text-lg text-slate-100">{t("public.contact.whatsapp.title")}</h3>
              <p className="text-xs text-slate-400">{t("public.contact.whatsapp.subtitle")}</p>
            </div>
          </div>
          {cfg?.whatsapp_url ? (
            <a href={cfg.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-cta"
               className="btn-scale inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold shadow-lg shadow-emerald-500/40">
              <MessageCircle className="w-4 h-4" /> {t("public.contact.whatsapp.cta")}
            </a>
          ) : (
            <div className="text-xs text-slate-500" data-testid="contact-whatsapp-fallback">
              {t("public.contact.whatsapp.fallback_pre")} {cfg?.contact_email ? <>{t("public.contact.whatsapp.fallback_email_pre")} <a href={`mailto:${cfg.contact_email}`} className="text-blue-400 hover:text-blue-300">{cfg.contact_email}</a>.</> : null}
            </div>
          )}
        </div>

        {/* Calendly booking */}
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-6" data-testid="contact-calendly-card">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 grid place-items-center rounded-md bg-blue-500/15 text-blue-300"><CalendarClock className="w-5 h-5" /></span>
            <div>
              <h3 className="font-display font-semibold text-lg text-slate-100">{t("public.contact.calendly.title")}</h3>
              <p className="text-xs text-slate-400">{t("public.contact.calendly.subtitle")}</p>
            </div>
          </div>
          {cfg?.calendly_url ? (
            <>
              <a href={cfg.calendly_url} target="_blank" rel="noopener noreferrer" data-testid="contact-calendly-cta"
                 className="btn-scale inline-flex items-center gap-2 h-10 px-4 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold shadow-lg shadow-blue-500/40">
                <CalendarClock className="w-4 h-4" /> {t("public.contact.calendly.open")}
              </a>
              <div className="mt-4 rounded-md overflow-hidden border border-slate-800 aspect-video" data-testid="contact-calendly-embed">
                <iframe title="Book a call" src={cfg.calendly_url} className="w-full h-full" loading="lazy" />
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-500" data-testid="contact-calendly-fallback">
              {t("public.contact.calendly.fallback")}
            </div>
          )}
        </div>
      </section>

      {/* Interactive map */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 pb-24" data-testid="contact-map-section">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <div className="p-5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 grid place-items-center rounded-md bg-blue-500/10 text-blue-400"><MapPin className="w-5 h-5" /></span>
              <div>
                <h3 className="font-display font-semibold text-lg text-slate-100">{t("public.contact.map.title")}</h3>
                {cfg?.map_address ? (
                  <p className="text-xs text-slate-400" data-testid="contact-map-address">{cfg.map_address}</p>
                ) : (
                  <p className="text-xs text-slate-500">{t("public.contact.map.address_soon")}</p>
                )}
              </div>
            </div>
            {cfg?.map_directions_url && (
              <a href={cfg.map_directions_url} target="_blank" rel="noopener noreferrer" data-testid="contact-map-directions"
                 className="btn-scale h-9 px-3 rounded-md border border-slate-700 hover:border-blue-500/50 text-slate-200 text-xs font-semibold inline-flex items-center gap-1">
                {t("public.contact.map.directions")} <ArrowRight className="w-3 h-3" />
              </a>
            )}
          </div>
          {cfg?.map_embed_url ? (
            <div className="aspect-[16/7] border-t border-slate-800" data-testid="contact-map-embed">
              <iframe title="Studio location" src={cfg.map_embed_url} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          ) : (
            <div className="aspect-[16/7] border-t border-slate-800 grid place-items-center bg-slate-950/60 text-center text-slate-400 px-6" data-testid="contact-map-fallback">
              <div>
                <div className="text-sm font-semibold text-slate-200 mb-1">{cfg?.map_fallback_text || t("public.contact.map.fallback_line1")}</div>
                <div className="text-xs text-slate-500">{t("public.contact.map.fallback_line2")}</div>
              </div>
            </div>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
